import { NSARouterMakerResult } from "namirasoft-account-react";
import { BaseMetaColumn, BaseMetaTable, BaseServerConfig, FilterItem, FilterItemOperator, IStorageLocal, SortItem, TimeOperation } from "namirasoft-core";
import { AccountRow, CurrencyRow, GeneralLedgerCurrencyMoneyRow, GeneralLedgerCurrencyRow, GeneralLedgerRow, GeneralLedgerRowRow, LockInputRow, LockRow, NamirasoftERPMetaDatabase, PartyRow, PartyTypeRow, TagRow } from "namirasoft-erp";
import { NamirasoftMap } from "namirasoft-site-map";
import { BaseColumnFormatter, DateTimeFormatter, FloatFormatter, IDFormatter, NSBox, NSBoxBoolean, NSBoxDate, NSBoxEntity, NSButtonBlue, NSColumn, NSDialog, NSLine, NSLoading, NSPanel, NSRow, NSSpace, NSSpaceSizeType, NSTable, StringFormatter, StringFormatterSizeType, TableColumnInfo, TableRowInfo } from "namirasoft-site-react";
import { Component, createRef } from "react";
import { ErpLayout } from "../ErpLayout";
import { ErpRouterProps } from "../ErpRouterProps";
import { ErpScope, ErpScopeField, ErpScopeMode } from "../ErpScope";
import Styles from "./GeneralLedgerReportPage.module.css";

interface GeneralLedgerReportPageProps extends NSARouterMakerResult, ErpRouterProps { }

interface TableRow
{
    line_id: string;
    journal_entry_id: string | null;
    date_time: Date | string | null;
    description: string | null;
    account_id: string | null;
    party_type_id: string | null;
    party_id: string | null;
    tag_ids: string[];
    currency_id: string | null;
    currency_symbol: string | null;
    debit: number | string | null;
    credit: number | string | null;
    checked: boolean;
    marked: boolean;
    journal_entry_finalized: boolean;
    __is_marked_balance?: boolean;
    __money_by_currency?: Map<string, GeneralLedgerCurrencyMoneyRow>;
    [balanceKey: string]: unknown;
}

interface GeneralLedgerReportPageState
{
    loading: boolean;
    report: GeneralLedgerRow | null;
    currencySymbol: string;
    allowedPartyTypeIds: string[] | null;
    scopeVersion: number;
    lockDialogRow: TableRow | null;
    lockDialogLocks: LockRow[] | null;
    lockDialogInclude: { account: boolean; party_type: boolean; party: boolean };
    pageLocks: LockRow[] | null;
}

type ScopedTable = BaseMetaTable & {
    columns: {
        company_id?: BaseMetaColumn;
        branch_id?: BaseMetaColumn;
        enabled: BaseMetaColumn;
    };
};

type ListFn<TRow> = (
    filters: FilterItem[] | null,
    page: number | null,
    size: number | null,
    sorts: SortItem[],
    all_workspaces: boolean | null,
    config?: BaseServerConfig<any>,
) => Promise<{ rows: TRow[]; count: number }>;

const allowedPartyTypesCache = new Map<string, Promise<string[]>>();

export default class GeneralLedgerReportPage extends Component<GeneralLedgerReportPageProps, GeneralLedgerReportPageState>
{
    override state: GeneralLedgerReportPageState = {
        loading: false,
        report: null,
        currencySymbol: "",
        allowedPartyTypeIds: null,
        scopeVersion: 0,
        lockDialogRow: null,
        lockDialogLocks: null,
        lockDialogInclude: { account: false, party_type: false, party: false },
        pageLocks: null,
    };

    IncludeAccount = createRef<NSBoxBoolean>();
    IncludePartyType = createRef<NSBoxBoolean>();
    IncludeParty = createRef<NSBoxBoolean>();

    private unsubscribeScope: (() => void) | null = null;
    private lastCompanyId: string | null = null;
    private lastBranchId: string | null = null;

    FromDate = createRef<NSBoxDate>();
    ToDate = createRef<NSBoxDate>();
    Account = createRef<NSBoxEntity<AccountRow>>();
    PartyType = createRef<NSBoxEntity<PartyTypeRow>>();
    Party = createRef<NSBoxEntity<PartyRow>>();
    Currency = createRef<NSBoxEntity<CurrencyRow>>();
    Tags = createRef<NSBoxEntity<TagRow>>();
    HasParty = createRef<NSBoxBoolean>();
    HasTag = createRef<NSBoxBoolean>();
    ShowDebit = createRef<NSBoxBoolean>();
    ShowCredit = createRef<NSBoxBoolean>();
    ConvertToBranchCurrency = createRef<NSBoxBoolean>();
    Table = createRef<NSTable<TableRow>>();

    override componentDidMount(): void
    {
        this.FromDate.current?.setValue(TimeOperation.weeksLater(-1, new Date()));
        this.ToDate.current?.setValue(new Date());
        this.lastCompanyId = ErpScope.getCompanyIdRaw();
        this.lastBranchId = ErpScope.getBranchIdRaw();

        this.unsubscribeScope = ErpScope.subscribe(() =>
        {
            let companyId = ErpScope.getCompanyIdRaw();
            let branchId = ErpScope.getBranchIdRaw();
            let scopeChanged = companyId !== this.lastCompanyId || branchId !== this.lastBranchId;
            this.lastCompanyId = companyId;
            this.lastBranchId = branchId;

            this.setState(s => ({ scopeVersion: s.scopeVersion + 1, report: null }));
            if (!scopeChanged)
                return;

            this.Account.current?.setValueAll([]);
            this.PartyType.current?.setValueAll([]);
            this.Party.current?.setValueAll([]);
            this.Currency.current?.setValueOne(null);
            this.Tags.current?.setValueAll([]);
            this.PartyType.current?.setDisabled(true);
            this.setState({ allowedPartyTypeIds: null });
        });
    }

    override componentWillUnmount(): void
    {
        this.unsubscribeScope?.();
    }

    private loadAllowedPartyTypes(accountId: string): Promise<string[]>
    {
        let cached = allowedPartyTypesCache.get(accountId);
        if (cached)
            return cached;
        let promise = this.props.servers.erp.account.Get(accountId)
            .then(a => a.party_type_ids)
            .catch(err => { allowedPartyTypesCache.delete(accountId); throw err; });
        allowedPartyTypesCache.set(accountId, promise);
        return promise;
    }

    private onAccountChanged = async (): Promise<void> =>
    {
        let accountIds = this.Account.current?.getValueAll() ?? [];
        this.PartyType.current?.setValueAll([]);
        this.Party.current?.setValueAll([]);

        if (accountIds.length === 0)
        {
            this.PartyType.current?.setDisabled(false);
            this.setState({ allowedPartyTypeIds: null });
            return;
        }
        this.PartyType.current?.setDisabled(false);
        let lists = await Promise.all(accountIds.map(id => this.loadAllowedPartyTypes(id)));
        let union = Array.from(new Set(lists.flat()));
        this.setState({ allowedPartyTypeIds: union });
    };

    private onPartyTypeChanged = (): void =>
    {
        this.Party.current?.setValueAll([]);
        this.Party.current?.reload();
    };

    private refreshCurrencySymbol(): void
    {
        let currencyId = this.Currency.current?.getValueOne(false) ?? null;
        if (!currencyId) { this.setState({ currencySymbol: "" }); return; }
        this.props.servers.erp.currency.Get(currencyId)
            .then(c => this.setState({ currencySymbol: c?.symbol ?? "" }))
            .catch(() => this.setState({ currencySymbol: "" }));
    }

    private runReport = (onFinished: () => void): void =>
    {
        let companyId: string | null;
        let branchId: string | null;
        let fiscalYearId: string | null;
        try
        {
            companyId = ErpScope.getCompanyIdOrNull();
            branchId = ErpScope.getBranchIdOrNull();
            fiscalYearId = ErpScope.getFiscalYearIdOrNull();
        }
        catch (err)
        {
            this.props.notifier.onError(err as Error);
            onFinished();
            return;
        }

        let fromRaw = this.FromDate.current?.getValue(false) ?? null;
        let toRaw = this.ToDate.current?.getValue(false) ?? null;

        let accountIds = this.Account.current?.getValueAll() ?? [];
        let partyTypeIds = this.PartyType.current?.getValueAll() ?? [];
        let partyIds = this.Party.current?.getValueAll() ?? [];
        let tagIds = this.Tags.current?.getValueAll() ?? [];
        let currencyId = this.Currency.current?.getValueOne(false) ?? null;
        let hasParty = this.HasParty.current?.getValueNull(false) ?? null;
        let hasTag = this.HasTag.current?.getValueNull(false) ?? null;
        let showDebit = this.ShowDebit.current?.getValueNull(false) ?? true;
        let showCredit = this.ShowCredit.current?.getValueNull(false) ?? true;
        let convertToBranchCurrency = this.ConvertToBranchCurrency.current?.getValueNull(false) ?? false;

        let orCsv = (arr: string[]): string | null => arr.length > 0 ? arr.join(",") : null;

        this.refreshCurrencySymbol();
        this.setState({ loading: true });

        this.props.servers.erp.report.GeneralLedger(
            companyId,
            branchId,
            fiscalYearId,
            fromRaw ? new Date(fromRaw) : null,
            toRaw ? new Date(toRaw) : null,
            orCsv(accountIds),
            orCsv(partyTypeIds),
            orCsv(partyIds),
            orCsv(tagIds),
            currencyId,
            hasParty,
            hasTag,
            showDebit,
            showCredit,
            convertToBranchCurrency,
        ).then((res: GeneralLedgerRow) =>
        {
            this.setState({ loading: false, report: res });
            this.loadPageLocks();
            onFinished();
        }).catch(() =>
        {
            // The server already reported the error through the notifier.
            this.setState({ loading: false });
            onFinished();
        });
    };

    private refreshTableRows = async (): Promise<void> =>
    {
        let { rows, count } = await this.getRows();
        this.Table.current?.setRows(rows, count);
    };

    private openLockDialog = (row: TableRow): void =>
    {
        this.setState({
            lockDialogRow: row,
            lockDialogLocks: null,
            lockDialogInclude: {
                account: !!row.account_id,
                party_type: !!row.party_type_id,
                party: !!row.party_id,
            },
        });
    };

    private closeLockDialog = (): void =>
    {
        this.setState({ lockDialogRow: null, lockDialogLocks: null });
    };

    private getLockKey(): { accountId: string | null; partyTypeId: string | null; partyId: string | null }
    {
        let row = this.state.lockDialogRow;
        let inc = this.state.lockDialogInclude;
        return {
            accountId: row && inc.account ? (row.account_id ?? null) : null,
            partyTypeId: row && inc.party_type ? (row.party_type_id ?? null) : null,
            partyId: row && inc.party ? (row.party_id ?? null) : null,
        };
    }

    private findMatchingLock(): LockRow | null
    {
        let locks = this.state.lockDialogLocks;
        if (!locks)
            return null;
        let { accountId, partyTypeId, partyId } = this.getLockKey();
        return locks.find(l =>
            (l.account_id ?? null) === accountId
            && (l.party_type_id ?? null) === partyTypeId
            && (l.party_id ?? null) === partyId
        ) ?? null;
    }

    private loadPageLocks = (): void =>
    {
        let companyId = ErpScope.getCompanyIdOrNull();
        let branchId = ErpScope.getBranchIdOrNull();
        if (!companyId || !branchId)
        {
            this.setState({ pageLocks: [] });
            return;
        }
        let db = new NamirasoftERPMetaDatabase(this.props.account.token_manager, console.error);
        let lockTbl = db.tables.lock;
        let filters: FilterItem[] = [
            new FilterItem(lockTbl, lockTbl.columns.company_id, false, FilterItemOperator.all.equals, companyId),
            new FilterItem(lockTbl, lockTbl.columns.branch_id, false, FilterItemOperator.all.equals, branchId),
            new FilterItem(lockTbl, lockTbl.columns.enabled, false, FilterItemOperator.all.equals, "1"),
        ];
        this.props.servers.erp.lock.List(filters, null, null, [], null)
            .then(res => this.setState({ pageLocks: res.rows }))
            .catch(() => this.setState({ pageLocks: [] }));
    };

    private findLockForRow(row: TableRow): LockRow | null
    {
        let locks = this.state.pageLocks;
        if (!locks || locks.length === 0)
            return null;
        if (!row.date_time)
            return null;
        let rowTime = new Date(row.date_time).getTime();
        let rowAccountId = row.account_id ?? null;
        let rowPartyTypeId = row.party_type_id ?? null;
        let rowPartyId = row.party_id ?? null;
        return locks.find(l =>
        {
            if (!l.to_date_time)
                return false;
            if (new Date(l.to_date_time).getTime() < rowTime)
                return false;
            let lAccount = l.account_id ?? null;
            let lPartyType = l.party_type_id ?? null;
            let lParty = l.party_id ?? null;
            if (lAccount !== null && lAccount !== rowAccountId) return false;
            if (lPartyType !== null && lPartyType !== rowPartyTypeId) return false;
            if (lParty !== null && lParty !== rowPartyId) return false;
            return true;
        }) ?? null;
    }

    private loadLocks = (onFinished: () => void): void =>
    {
        let companyId: string | null;
        let branchId: string | null;
        try
        {
            companyId = ErpScope.getCompanyId();
            branchId = ErpScope.getBranchId();
        }
        catch (err)
        {
            this.props.notifier.onError(err as Error);
            onFinished();
            return;
        }

        let db = new NamirasoftERPMetaDatabase(this.props.account.token_manager, console.error);
        let lockTbl = db.tables.lock;
        let filters: FilterItem[] = [
            new FilterItem(lockTbl, lockTbl.columns.company_id, false, FilterItemOperator.all.equals, companyId),
            new FilterItem(lockTbl, lockTbl.columns.branch_id, false, FilterItemOperator.all.equals, branchId),
            new FilterItem(lockTbl, lockTbl.columns.enabled, false, FilterItemOperator.all.equals, "1"),
        ];

        this.props.servers.erp.lock.List(filters, null, null, [], null)
            .then(res =>
            {
                this.setState({ lockDialogLocks: res.rows });
                onFinished();
            })
            .catch(() =>
            {
                // The server already reported the error through the notifier.
                onFinished();
            });
    };

    private saveLock = (onFinished: () => void): void =>
    {
        let row = this.state.lockDialogRow;
        if (!row) { onFinished(); return; }

        let companyId: string;
        let branchId: string;
        try
        {
            companyId = ErpScope.getCompanyId();
            branchId = ErpScope.getBranchId();
        }
        catch (err)
        {
            this.props.notifier.onError(err as Error);
            onFinished();
            return;
        }

        let { accountId, partyTypeId, partyId } = this.getLockKey();
        let toDateTime = row.date_time ? new Date(row.date_time) : new Date();

        let body: LockInputRow = {
            company_id: companyId,
            branch_id: branchId,
            account_id: accountId,
            party_type_id: partyTypeId,
            party_id: partyId,
            to_date_time: toDateTime,
            enabled: true,
            description: null,
            lock_category: [],
            lock_tag: [],
            lock_field: [],
        };

        let match = this.findMatchingLock();
        let action = match
            ? this.props.servers.erp.lock.Update(match.id, body)
            : this.props.servers.erp.lock.Create(body);

        action
            .then(() =>
            {
                this.closeLockDialog();
                this.loadPageLocks();
                this.refreshTableRows();
                onFinished();
            })
            .catch(() =>
            {
                // The server already reported the error through the notifier.
                onFinished();
            });
    };

    private toggleMarked = async (journalEntryId: string, lineId: string, currentlyMarked: boolean): Promise<void> =>
    {
        let { report } = this.state;
        if (!report)
            return;
        let next = !currentlyMarked;
        let updated: GeneralLedgerRow = { ...report, rows: report.rows.map(r => r.line_id === lineId ? { ...r, marked: next } : r) };
        this.setState({ report: updated }, () => { this.refreshTableRows(); });
        try
        {
            await this.props.servers.erp.journal_entry_line.SetMarked(journalEntryId, lineId, { marked: next });
        }
        catch
        {
            // The server already reported the error through the notifier.
            let reverted: GeneralLedgerRow = { ...report, rows: report.rows.map(r => r.line_id === lineId ? { ...r, marked: currentlyMarked } : r) };
            this.setState({ report: reverted }, () => { this.refreshTableRows(); });
        }
    };

    private toggleChecked = async (journalEntryId: string, lineId: string, currentlyChecked: boolean): Promise<void> =>
    {
        let { report } = this.state;
        if (!report)
            return;
        let next = !currentlyChecked;
        let updated: GeneralLedgerRow = { ...report, rows: report.rows.map(r => r.line_id === lineId ? { ...r, checked: next } : r) };
        this.setState({ report: updated }, () => { this.refreshTableRows(); });
        try
        {
            await this.props.servers.erp.journal_entry_line.SetChecked(journalEntryId, lineId, { checked: next });
        }
        catch
        {
            // The server already reported the error through the notifier.
            let reverted: GeneralLedgerRow = { ...report, rows: report.rows.map(r => r.line_id === lineId ? { ...r, checked: currentlyChecked } : r) };
            this.setState({ report: reverted }, () => { this.refreshTableRows(); });
        }
    };

    private formatMoney(value: string | number, symbolOverride?: string): string
    {
        let n = Number(value) || 0;
        let formatted = n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        let raw = symbolOverride !== undefined ? symbolOverride : this.state.currencySymbol;
        let symbol = raw?.trim() ?? "";
        return symbol ? `${symbol} ${formatted}` : formatted;
    }

    private static readonly OPENING_ROW_ID = "__opening__";
    private static readonly PERIOD_ROW_ID = "__period__";
    private static readonly CLOSING_ROW_ID = "__closing__";
    private static readonly MARKED_BALANCE_PREFIX = "__marked_balance__";

    private getCurrencies(): GeneralLedgerCurrencyRow[]
    {
        return this.state.report?.currencies ?? [];
    }

    private balanceColName(currency_id: string, kind: "balance_debit" | "balance_credit" | "balance"): string
    {
        return `bal_${currency_id}_${kind}`;
    }

    private buildColumns(): TableColumnInfo[]
    {
        let db = new NamirasoftERPMetaDatabase(this.props.account.token_manager, console.error);
        let namirasoft = new NamirasoftMap(this.props.account.token_manager, console.error);
        let je = db.tables.journal_entry;
        let line = db.tables.journal_entry_line;
        let checkedCol = new BaseMetaColumn(line, "checked", "✓", line.columns.checked.type, true);
        let markedCol = new BaseMetaColumn(line, "marked", "▬", line.columns.marked.type, true);
        let cols: TableColumnInfo[] = [
            BaseColumnFormatter.getColumn(checkedCol, new StringFormatter("24px")),
            BaseColumnFormatter.getColumn(markedCol, new StringFormatter("24px")),
            BaseColumnFormatter.getColumn(je.columns.date_time, new DateTimeFormatter()),
            BaseColumnFormatter.getColumn(line.columns.journal_entry_id, new IDFormatter(namirasoft, null)),
            BaseColumnFormatter.getColumn(je.columns.description, new StringFormatter(StringFormatterSizeType.Sentence)),
        ];
        cols.push(BaseColumnFormatter.getColumn(line.columns.account_id, new IDFormatter(namirasoft, new IStorageLocal())));
        let partyTypeCol = new BaseMetaColumn(line, "party_type_id", "Party Type", line.columns.party_id.type, true);
        cols.push(BaseColumnFormatter.getColumn(partyTypeCol, new IDFormatter(namirasoft, new IStorageLocal())));
        cols.push(BaseColumnFormatter.getColumn(line.columns.party_id, new IDFormatter(namirasoft, new IStorageLocal())));
        cols.push(BaseColumnFormatter.getColumn(line.columns.tag_ids, new IDFormatter(namirasoft, new IStorageLocal())));
        cols.push(BaseColumnFormatter.getColumn(line.columns.debit, new FloatFormatter("150px")));
        cols.push(BaseColumnFormatter.getColumn(line.columns.credit, new FloatFormatter("150px")));

        for (let c of this.getCurrencies())
        {
            let sym = c.symbol?.trim() || c.id;
            let bd = new BaseMetaColumn(line, this.balanceColName(c.id, "balance_debit"), `Balance Debit (${sym})`, line.columns.debit.type, true);
            let bc = new BaseMetaColumn(line, this.balanceColName(c.id, "balance_credit"), `Balance Credit (${sym})`, line.columns.credit.type, true);
            cols.push(BaseColumnFormatter.getColumn(bd, new FloatFormatter("150px")));
            cols.push(BaseColumnFormatter.getColumn(bc, new FloatFormatter("150px")));
        }

        let lockCol = new BaseMetaColumn(line, "lock", "🔒", line.columns.marked.type, true);
        cols.push(BaseColumnFormatter.getColumn(lockCol, new StringFormatter("40px")));
        let finalizedCol = new BaseMetaColumn(line, "finalized", "✔", line.columns.marked.type, true);
        cols.push(BaseColumnFormatter.getColumn(finalizedCol, new StringFormatter("40px")));
        return cols;
    }

    private toggleFinalized = async (journalEntryId: string, currentlyFinalized: boolean): Promise<void> =>
    {
        let { report } = this.state;
        if (!report)
            return;
        let next = !currentlyFinalized;
        let updated: GeneralLedgerRow = { ...report, rows: report.rows.map(r => r.journal_entry_id === journalEntryId ? { ...r, journal_entry_finalized: next } : r) };
        this.setState({ report: updated }, () => { this.refreshTableRows(); });
        try
        {
            await this.props.servers.erp.journal_entry.SetFinalized(journalEntryId, { finalized: next });
        }
        catch
        {
            // The server already reported the error through the notifier.
            let reverted: GeneralLedgerRow = { ...report, rows: report.rows.map(r => r.journal_entry_id === journalEntryId ? { ...r, journal_entry_finalized: currentlyFinalized } : r) };
            this.setState({ report: reverted }, () => { this.refreshTableRows(); });
        }
    };

    private getRows = async (): Promise<{ rows: TableRow[]; count: number }> =>
    {
        let report = this.state.report;
        if (!report)
            return { rows: [], count: 0 };

        let sumDebit = (arr: GeneralLedgerCurrencyMoneyRow[]): number => arr.reduce((s, m) => s + (Number(m.debit) || 0), 0);
        let sumCredit = (arr: GeneralLedgerCurrencyMoneyRow[]): number => arr.reduce((s, m) => s + (Number(m.credit) || 0), 0);

        let mkSpecial = (id: string, moneyArr: GeneralLedgerCurrencyMoneyRow[]): TableRow =>
        {
            let money = moneyArr ?? [];
            let base: TableRow = {
                line_id: id,
                journal_entry_id: null,
                date_time: null,
                description: null,
                account_id: null,
                party_type_id: null,
                party_id: null,
                tag_ids: [],
                currency_id: null,
                currency_symbol: null,
                debit: String(sumDebit(money)),
                credit: String(sumCredit(money)),
                checked: false,
                marked: false,
                journal_entry_finalized: false,
                __money_by_currency: new Map<string, GeneralLedgerCurrencyMoneyRow>(),
            };
            for (let m of money)
            {
                base[this.balanceColName(m.currency_id, "balance_debit")] = m.balance_debit ?? null;
                base[this.balanceColName(m.currency_id, "balance_credit")] = m.balance_credit ?? null;
                base[this.balanceColName(m.currency_id, "balance")] = m.balance ?? null;
                base.__money_by_currency?.set(m.currency_id, m);
            }
            return base;
        };

        let mkData = (r: GeneralLedgerRowRow): TableRow =>
        {
            let row: TableRow = {
                line_id: r.line_id,
                journal_entry_id: r.journal_entry_id,
                date_time: r.journal_entry_date_time,
                description: r.journal_entry_description,
                account_id: r.account_id,
                party_type_id: r.party_type_id,
                party_id: r.party_id,
                tag_ids: r.tag_ids ?? [],
                currency_id: r.currency_id,
                currency_symbol: r.currency_symbol ?? null,
                debit: r.debit,
                credit: r.credit,
                checked: !!r.checked,
                marked: !!r.marked,
                journal_entry_finalized: !!r.journal_entry_finalized,
            };
            for (let b of r.balances ?? [])
            {
                row[this.balanceColName(b.currency_id, "balance_debit")] = b.balance_debit;
                row[this.balanceColName(b.currency_id, "balance_credit")] = b.balance_credit;
                row[this.balanceColName(b.currency_id, "balance")] = b.balance;
            }
            return row;
        };

        let mkMarkedBalance = (r: GeneralLedgerRowRow): TableRow =>
        {
            let row: TableRow = {
                line_id: GeneralLedgerReportPage.MARKED_BALANCE_PREFIX + r.line_id,
                journal_entry_id: null,
                date_time: null,
                description: null,
                account_id: null,
                party_type_id: null,
                party_id: null,
                tag_ids: [],
                currency_id: null,
                currency_symbol: null,
                debit: null,
                credit: null,
                checked: false,
                marked: false,
                journal_entry_finalized: false,
                __is_marked_balance: true,
            };
            for (let b of r.balances ?? [])
            {
                row[this.balanceColName(b.currency_id, "balance_debit")] = b.balance_debit;
                row[this.balanceColName(b.currency_id, "balance_credit")] = b.balance_credit;
                row[this.balanceColName(b.currency_id, "balance")] = b.balance;
            }
            return row;
        };

        let dataRows: TableRow[] = [];
        for (let r of report.rows ?? [])
        {
            dataRows.push(mkData(r));
            if (r.marked)
                dataRows.push(mkMarkedBalance(r));
        }

        let rows: TableRow[] = [
            mkSpecial(GeneralLedgerReportPage.OPENING_ROW_ID, report.opening ?? []),
            ...dataRows,
            mkSpecial(GeneralLedgerReportPage.PERIOD_ROW_ID, report.period ?? []),
            mkSpecial(GeneralLedgerReportPage.CLOSING_ROW_ID, report.closing ?? []),
        ];
        return { rows, count: rows.length };
    };

    private getCellFormattedValue = (value: any, column: TableColumnInfo, row: TableRowInfo<TableRow>, printable: boolean): any =>
    {
        let id: string = row.value.line_id;
        let isSpecial = id === GeneralLedgerReportPage.OPENING_ROW_ID
            || id === GeneralLedgerReportPage.PERIOD_ROW_ID
            || id === GeneralLedgerReportPage.CLOSING_ROW_ID;
        let isMarkedBalance = !!row.value.__is_marked_balance;

        let rowSymbol: string | undefined = row.value.currency_symbol ?? undefined;

        let balMatch = column.name.match(/^bal_(.+)_(balance_debit|balance_credit|balance)$/);
        if (balMatch)
        {
            let colCurrencyId = balMatch[1];
            let currencies: GeneralLedgerCurrencyRow[] = this.state.report?.currencies ?? [];
            let colSymbol = currencies.find(c => c.id === colCurrencyId)?.symbol ?? "";
            let nullish = value === null || value === undefined;
            if (isMarkedBalance)
            {
                let formatted = this.formatMoney(nullish ? 0 : value, colSymbol);
                let isZero = nullish || Number(value) === 0;
                return <span style={{ fontWeight: "bold", color: isZero ? "#bdbdbd" : undefined }}>{formatted}</span>;
            }
            if (nullish)
                return isSpecial ? "" : <span className={Styles.muted}>{this.formatMoney(0, colSymbol)}</span>;
            let formatted = this.formatMoney(value, colSymbol);
            let isZero = Number(value) === 0;
            return isZero ? <span className={Styles.muted}>{formatted}</span> : formatted;
        }

        if (isMarkedBalance)
        {
            if (column.name === "description")
                return <span className={Styles.periodLabel}>Period Open / Close</span>;
            return "";
        }

        if (column.name === "lock")
        {
            if (isSpecial)
                return "";
            let lock = this.findLockForRow(row.value);
            let isLocked = !!lock;
            return (
                <span
                    onClick={(e) => { e.stopPropagation(); this.openLockDialog(row.value); }}
                    title={isLocked && lock ? `Locked until ${new Date(lock.to_date_time).toLocaleString()}` : "Lock up to this entry's date"}
                    className={`${Styles.iconCell} ${Styles.iconCellWide}`}
                    style={{ color: isLocked ? "#c19a00" : "#cfcfcf" }}
                >
                    {isLocked ? "🔒" : "○"}
                </span>
            );
        }

        if (column.name === "finalized")
        {
            if (isSpecial)
                return "";
            let isFinalized = !!row.value.journal_entry_finalized;
            let journalEntryId = row.value.journal_entry_id;
            if (!journalEntryId)
                return "";
            return (
                <span
                    onClick={(e) => { e.stopPropagation(); this.toggleFinalized(journalEntryId, isFinalized); }}
                    title={isFinalized ? "Unset Finalized" : "Set Finalized"}
                    className={`${Styles.iconCell} ${Styles.iconCellWide}`}
                    style={{ color: isFinalized ? "#2e7d32" : "#cfcfcf" }}
                >
                    {isFinalized ? "✔" : "○"}
                </span>
            );
        }

        if (column.name === "checked")
        {
            if (isSpecial)
                return "";
            let isChecked = !!row.value.checked;
            let lineId = row.value.line_id;
            let journalEntryId = row.value.journal_entry_id;
            if (!journalEntryId)
                return "";
            return (
                <span
                    onClick={(e) => { e.stopPropagation(); this.toggleChecked(journalEntryId, lineId, isChecked); }}
                    title={isChecked ? "Uncheck" : "Check"}
                    className={`${Styles.iconCell} ${Styles.iconCellNarrow}`}
                    style={{ color: isChecked ? "#2e7d32" : "#cfcfcf" }}
                >
                    {isChecked ? "✓" : "○"}
                </span>
            );
        }

        if (column.name === "marked")
        {
            if (isSpecial)
                return "";
            let isMarked = !!row.value.marked;
            let lineId = row.value.line_id;
            let journalEntryId = row.value.journal_entry_id;
            if (!journalEntryId)
                return "";
            return (
                <span
                    onClick={(e) => { e.stopPropagation(); this.toggleMarked(journalEntryId, lineId, isMarked); }}
                    title={isMarked ? "Unmark divider" : "Mark divider"}
                    className={`${Styles.iconCell} ${Styles.iconCellNarrow}`}
                    style={{ color: isMarked ? "#141B5C" : "#cfcfcf" }}
                >
                    ▬
                </span>
            );
        }

        if (isSpecial)
        {
            if (column.name === "date_time")
            {
                if (id === GeneralLedgerReportPage.OPENING_ROW_ID)
                    return "Opening Balance";
                if (id === GeneralLedgerReportPage.PERIOD_ROW_ID)
                    return "Period Total";
                return "Closing Balance";
            }
            if (column.name === "debit" || column.name === "credit")
            {
                let formatted = this.formatMoney(value);
                let isZero = !value || Number(value) === 0;
                return isZero ? <span className={Styles.muted}>{formatted}</span> : formatted;
            }
            return "";
        }

        if (column.name === "debit" || column.name === "credit")
        {
            let formatted = this.formatMoney(value, rowSymbol);
            let isZero = !value || Number(value) === 0;
            return isZero ? <span className={Styles.muted}>{formatted}</span> : formatted;
        }
        if (column.name === "description" && (value === null || value === undefined || value === ""))
            return <span className={Styles.muted}>-</span>;

        return column.formatter.format(value, column, row, printable);
    };

    private getRowAttributes = (row_value: TableRow, _row_index: number): { [key: string]: any } =>
    {
        let id: string = row_value.line_id;
        let isOpening = id === GeneralLedgerReportPage.OPENING_ROW_ID;
        let isPeriod = id === GeneralLedgerReportPage.PERIOD_ROW_ID;
        let isClosing = id === GeneralLedgerReportPage.CLOSING_ROW_ID;
        if (row_value.__is_marked_balance)
            return { "data-marked-row": "bottom", style: { background: "#dbe7ff", fontWeight: "bold" } };
        if (!isOpening && !isPeriod && !isClosing)
        {
            let style: React.CSSProperties = {};
            if (row_value.journal_entry_finalized)
                style.background = "#d4f7d4";
            else if (row_value.checked)
                style.background = "#fff8c4";
            let attrs: { [key: string]: any } = {};
            if (Object.keys(style).length > 0)
                attrs.style = style;
            return attrs;
        }

        let color = "hsla(234, 64%, 22%, 1)";
        let style: React.CSSProperties = { fontWeight: "bold", color, background: "#fafbff" };
        if (isOpening) style.borderBottom = "2px solid #141B5C";
        if (isClosing) style.borderTop = "2px solid #141B5C";
        return { style };
    };

    private renderTable(): React.ReactNode
    {
        let { report } = this.state;
        if (!report)
            return null;

        let currencyKey = (report.currencies ?? []).map(c => c.id).join(",");

        return (
            <div className={Styles.table}>
                <NSLine />
                <NSSpace size={NSSpaceSizeType.MINI} />
                <NSTable<TableRow>
                    ref={this.Table}
                    key={String(this.state.scopeVersion) + ":" + String(report.rows.length) + ":" + currencyKey}
                    name="general-ledger"
                    checkbox={false}
                    columns={this.buildColumns()}
                    getRows={this.getRows}
                    getRowKey={(row) => row.value.line_id}
                    getCellFormattedValue={this.getCellFormattedValue}
                    getRowAttributes={this.getRowAttributes}
                    sort={{ enabled: false }}
                    ui={{ pagination: false, grow: true }}
                />
            </div>
        );
    }

    override render(): React.ReactNode
    {
        let { servers } = this.props;
        let db = new NamirasoftERPMetaDatabase(this.props.account.token_manager, console.error);

        let scopedList = <TRow,>(table: ScopedTable, list: ListFn<TRow>): (filters: FilterItem[] | null, page: number | null, size: number | null, sorts: SortItem[]) => Promise<{ rows: TRow[]; count: number }> =>
        {
            return (filters, page, size, sorts) =>
            {
                let companyId = ErpScope.getCompanyIdOrNull();
                let branchId = ErpScope.getBranchIdOrNull();
                let f: FilterItem[] = filters ? [...filters] : [];
                if (companyId && table.columns.company_id)
                    f.push(new FilterItem(table, table.columns.company_id, false, FilterItemOperator.all.equals, companyId));
                if (branchId && table.columns.branch_id)
                    f.push(new FilterItem(table, table.columns.branch_id, false, FilterItemOperator.all.equals, branchId));
                f.push(new FilterItem(table, table.columns.enabled, false, FilterItemOperator.all.equals, "1"));
                return list(f, page, size, sorts, null);
            };
        };

        return (
            <ErpLayout {...this.props}
                mode={ErpScopeMode.Filter}
                fields={[ErpScopeField.Company, ErpScopeField.Branch, ErpScopeField.FiscalYear]}
                action={{ title: "General Ledger" }}
            >
                <>
                    <NSColumn style={{ width: "100%", alignItems: "center" }}>
                        <NSRow style={{ flexDirection: "row", flexWrap: "wrap", alignItems: "center" }}>
                            <NSBoxDate
                                ref={this.FromDate}
                                title="From Date" info={{ link: "https://namirasoft.com/erp/console-guide/general-ledger/#field-date-from" }}
                                required={false}
                            />
                            <NSBoxDate
                                ref={this.ToDate}
                                title="To Date" info={{ link: "https://namirasoft.com/erp/console-guide/general-ledger/#field-date-to" }}
                                required={false}
                            />
                            <NSBoxEntity<AccountRow>
                                ref={this.Account}
                                title="Account" info={{ link: "https://namirasoft.com/erp/console-guide/general-ledger/#field-account-ids" }}
                                required={false}
                                multiple={true}
                                table_name="account"
                                table={db.tables.account}
                                list={scopedList<AccountRow>(db.tables.account, servers.erp.account.List)}
                                getValue={(item) => item.id} getTitle={(item) => `${item.code} - ${item.name}`} getSort={() => null}
                                placeholder="Account"
                                onChanged={this.onAccountChanged}
                                style={{ width: NSBox.width.double }}
                            />
                            <NSBoxEntity<PartyTypeRow>
                                ref={this.PartyType}
                                title="Party Type" info={{ link: "https://namirasoft.com/erp/console-guide/general-ledger/#field-party-type-ids" }}
                                required={false}
                                multiple={true}
                                table_name="party_type"
                                table={db.tables.party_type}
                                list={async (filters, page, size, sorts) =>
                                {
                                    let scoped = scopedList<PartyTypeRow>(db.tables.party_type, servers.erp.party_type.List);
                                    let result = await scoped(filters, page, size, sorts);
                                    let allowed = this.state.allowedPartyTypeIds;
                                    if (allowed !== null)
                                    {
                                        let allowedSet = new Set(allowed);
                                        let rows = result.rows.filter(r => allowedSet.has(r.id));
                                        return { count: rows.length, rows };
                                    }
                                    return result;
                                }}
                                getValue={(item) => item.id} getTitle={(item) => item.name} getSort={() => null}
                                placeholder="Party Type"
                                onChanged={this.onPartyTypeChanged}
                            />
                            <NSBoxEntity<PartyRow>
                                ref={this.Party}
                                title="Party" info={{ link: "https://namirasoft.com/erp/console-guide/general-ledger/#field-party-ids" }}
                                required={false}
                                multiple={true}
                                table_name="party"
                                table={db.tables.party}
                                list={async (filters, page, size, sorts) =>
                                {
                                    let ptIds = this.PartyType.current?.getValueAll() ?? [];
                                    let scoped = scopedList<PartyRow>(db.tables.party, servers.erp.party.List);
                                    let result = await scoped(filters, page, size, sorts);
                                    if (ptIds.length > 0)
                                    {
                                        let allowedSet = new Set(ptIds);
                                        let rows = result.rows.filter(r => allowedSet.has(r.party_type_id));
                                        return { count: rows.length, rows };
                                    }
                                    return result;
                                }}
                                getValue={(item) => item.id} getTitle={(item) => item.name} getSort={() => null}
                                placeholder="Party"
                            />
                            <NSBoxEntity<TagRow>
                                ref={this.Tags}
                                title="Tags" info={{ link: "https://namirasoft.com/erp/console-guide/general-ledger/#field-tag-ids" }}
                                required={false}
                                multiple={true}
                                table_name="tag"
                                table={db.tables.tag}
                                list={scopedList<TagRow>(db.tables.tag, servers.erp.tag.List)}
                                getValue={(item) => item.id} getTitle={(item) => item.name} getSort={() => null}
                                placeholder="Tags"
                            />
                            <NSBoxEntity<CurrencyRow>
                                ref={this.Currency}
                                title="Currency" info={{ link: "https://namirasoft.com/erp/console-guide/general-ledger/#field-currency-id" }}
                                required={false}
                                multiple={false}
                                table_name="currency"
                                table={db.tables.currency}
                                list={(filters, page, size, sorts) =>
                                {
                                    let f: FilterItem[] = filters ? [...filters] : [];
                                    f.push(new FilterItem(db.tables.currency, db.tables.currency.columns.enabled, false, FilterItemOperator.all.equals, "1"));
                                    return servers.erp.currency.List(f, page, size, sorts, null);
                                }}
                                getValue={(item) => item.id} getTitle={(item) => item.name} getSort={() => null}
                                placeholder="Currency"
                            />
                            <NSPanel title="Filter" style={{ padding: 8, display: "flex", flexDirection: "column", gap: 4 }}>
                                <NSRow style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
                                    <NSBoxBoolean
                                        ref={this.HasParty}
                                        required={false}
                                        triState
                                        checked={null}
                                        title="Has Party" info={{ link: "https://namirasoft.com/erp/console-guide/general-ledger/#field-has-party" }}
                                        hideHeader
                                        style={{ width: 150, flexGrow: 0 }}
                                    />
                                    <NSBoxBoolean
                                        ref={this.HasTag}
                                        required={false}
                                        triState
                                        checked={null}
                                        title="Has Tag" info={{ link: "https://namirasoft.com/erp/console-guide/general-ledger/#field-has-tag" }}
                                        hideHeader
                                        style={{ width: 150, flexGrow: 0 }}
                                    />
                                    <NSBoxBoolean
                                        ref={this.ShowDebit}
                                        required={false}
                                        checked={true}
                                        title="Show Debit" info={{ link: "https://namirasoft.com/erp/console-guide/general-ledger/#field-show-debit" }}
                                        hideHeader
                                        style={{ width: 150, flexGrow: 0 }}
                                    />
                                    <NSBoxBoolean
                                        ref={this.ShowCredit}
                                        required={false}
                                        checked={true}
                                        title="Show Credit" info={{ link: "https://namirasoft.com/erp/console-guide/general-ledger/#field-show-credit" }}
                                        hideHeader
                                        style={{ width: 150, flexGrow: 0 }}
                                    />
                                </NSRow>
                            </NSPanel>
                            <NSPanel title="Conversion" style={{ padding: 8, display: "flex", flexDirection: "column", gap: 4 }}>
                                <NSRow style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
                                    <NSBoxBoolean
                                        ref={this.ConvertToBranchCurrency}
                                        required={false}
                                        checked={false}
                                        title="Convert To Branch Currency" info={{ link: "https://namirasoft.com/erp/console-guide/general-ledger/#field-convert-to-branch-currency" }}
                                        hideHeader
                                        style={{ width: 220, flexGrow: 0 }}
                                    />
                                </NSRow>
                            </NSPanel>
                            <NSButtonBlue
                                title="Run Report"
                                onClick={{ action: this.runReport, showLoading: true }}
                            />
                        </NSRow>

                        {this.state.loading && <NSLoading />}
                        {!this.state.loading && this.renderTable()}
                    </NSColumn>
                    {this.state.lockDialogRow && (() =>
                    {
                        let row = this.state.lockDialogRow;
                        let inc = this.state.lockDialogInclude;
                        let locks = this.state.lockDialogLocks;
                        let match = this.findMatchingLock();
                        let statusNode: React.ReactNode;
                        if (locks === null)
                            statusNode = <div style={{ fontSize: 12, color: "#888" }}>Click "Load Locks" to check if a matching lock already exists.</div>;
                        else if (match)
                            statusNode = <div style={{ fontSize: 12, color: "#1565c0" }}>Will UPDATE existing lock (currently expires {new Date(match.to_date_time).toLocaleString()}).</div>;
                        else
                            statusNode = <div style={{ fontSize: 12, color: "#2e7d32" }}>Will CREATE a new lock.</div>;
                        return (
                            <NSDialog onClose={this.closeLockDialog} center_dialog>
                                <NSColumn style={{ gap: 8, padding: 12, minWidth: 360 }}>
                                    <div style={{ fontWeight: "bold" }}>Lock up to {row.date_time ? new Date(row.date_time).toLocaleString() : ""}</div>
                                    <div style={{ fontSize: 12, color: "#666" }}>Choose which fields to include in the lock key.</div>
                                    <NSBoxBoolean
                                        ref={this.IncludeAccount}
                                        required={true}
                                        hideHeader
                                        checked={inc.account}
                                        title="Include Account"
                                        style={{ width: "100%" }}
                                        onChanged={(b) => this.setState({ lockDialogInclude: { ...this.state.lockDialogInclude, account: b.getValueBool(false) } })}
                                    />
                                    <NSBoxBoolean
                                        ref={this.IncludePartyType}
                                        required={true}
                                        hideHeader
                                        checked={inc.party_type}
                                        title="Include Party Type"
                                        style={{ width: "100%" }}
                                        onChanged={(b) => this.setState({ lockDialogInclude: { ...this.state.lockDialogInclude, party_type: b.getValueBool(false) } })}
                                    />
                                    <NSBoxBoolean
                                        ref={this.IncludeParty}
                                        required={true}
                                        hideHeader
                                        checked={inc.party}
                                        title="Include Party"
                                        style={{ width: "100%" }}
                                        onChanged={(b) => this.setState({ lockDialogInclude: { ...this.state.lockDialogInclude, party: b.getValueBool(false) } })}
                                    />
                                    {statusNode}
                                    <NSRow style={{ justifyContent: "center", gap: 8, marginTop: 8 }}>
                                        <NSButtonBlue
                                            title="Load Locks"
                                            onClick={{ action: this.loadLocks, showLoading: true }}
                                        />
                                        {
                                            locks !== null && (
                                                <NSButtonBlue
                                                    title={match ? "Update Lock" : "Create Lock"}
                                                    onClick={{ action: this.saveLock, showLoading: true }}
                                                />
                                            )
                                        }
                                    </NSRow>
                                </NSColumn>
                            </NSDialog>
                        );
                    })()}
                </>
            </ErpLayout>
        );
    }
}